import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import UploadZone from './components/UploadZone';
import ProcessingStatus from './components/ProcessingStatus';
import ExtractedDataForm from './components/ExtractedDataForm';
import { ocrService } from '../../services/ocrService';
import { transactionService } from '../../services/transactionService';
import { receiptService } from '../../services/receiptService';
import { realtimeService } from '../../services/realtimeService';
import { useAuth } from '../../contexts/AuthContext';

const ReceiptUploadOCRProcessing = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [uploadedFile, setUploadedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [extractedData, setExtractedData] = useState(null);
  const [error, setError] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [savedReceipts, setSavedReceipts] = useState([]);
  const [showReceiptsList, setShowReceiptsList] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [showReceiptModal, setShowReceiptModal] = useState(false);

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/dashboard' },
    { label: 'Receipt Upload', path: null }
  ];

  // Real-time subscription for transactions (receipts create transactions)
  useEffect(() => {
    if (!user) return;

    const transactionChannel = realtimeService?.subscribeToTransactions(
      () => {
        // Transaction created from receipt on another device
        console.log('New transaction detected from another device');
      },
      () => {
        // Transaction updated on another device
        console.log('Transaction updated on another device');
      },
      () => {
        // Transaction deleted on another device
        console.log('Transaction deleted on another device');
      }
    );

    return () => {
      realtimeService?.unsubscribe(transactionChannel);
    };
  }, [user]);

  // Load saved receipts
  useEffect(() => {
    if (user) {
      loadSavedReceipts();
    }
  }, [user]);

  const loadSavedReceipts = async () => {
    try {
      const receipts = await receiptService?.getAll();
      setSavedReceipts(receipts);
    } catch (err) {
      console.error('Error loading receipts:', err);
    }
  };

  const handleFileSelect = async (file) => {
    setError(null);
    
    // Validate file
    const validation = ocrService?.validateReceiptImage(file);
    if (!validation?.valid) {
      setError(validation?.message);
      return;
    }

    setUploadedFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader?.result);
    };
    reader?.readAsDataURL(file);
  };

  const handleProcessReceipt = async () => {
    if (!uploadedFile) return;

    setIsProcessing(true);
    setProcessingProgress(0);
    setError(null);

    try {
      // Simulate progress
      setProcessingProgress(20);
      
      // Process with OCR
      const result = await ocrService?.processReceipt(uploadedFile);
      
      setProcessingProgress(100);
      setExtractedData(result);
      setShowResults(true);
    } catch (err) {
      console.error('OCR processing error:', err);
      setError(err?.message || 'Failed to process receipt. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCreateTransaction = async (transactionData) => {
    try {
      // Create transaction
      const transaction = await transactionService?.create(transactionData);
      
      // Save receipt with image
      if (uploadedFile && extractedData) {
        const receiptData = {
          merchantName: extractedData?.merchantName,
          date: transactionData?.date,
          totalAmount: Math.abs(transactionData?.amount),
          taxAmount: extractedData?.taxAmount,
          overallConfidence: extractedData?.overallConfidence,
          transactionId: transaction?.id
        };
        
        await receiptService?.createReceipt(uploadedFile, receiptData);
      }
      
      navigate('/transactions-management', { 
        state: { message: 'Transaction created successfully from receipt' } 
      });
    } catch (err) {
      setError(err?.message || 'Failed to create transaction');
    }
  };

  const handleReset = () => {
    setUploadedFile(null);
    setImagePreview(null);
    setExtractedData(null);
    setError(null);
    setShowResults(false);
    setProcessingProgress(0);
  };

  const handleViewReceipt = async (receipt) => {
    setSelectedReceipt(receipt);
    setShowReceiptModal(true);
  };

  const handleDeleteReceipt = async (receiptId) => {
    if (!window.confirm('Are you sure you want to delete this receipt?')) return;
    
    try {
      await receiptService?.delete(receiptId);
      await loadSavedReceipts();
    } catch (err) {
      setError(err?.message || 'Failed to delete receipt');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Main Content with proper spacing */}
      <main className="container mx-auto px-4 py-8 mt-8">
        <div className="max-w-4xl mx-auto">
          {/* Breadcrumb Navigation */}
          <Breadcrumbs items={breadcrumbItems} />

          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <Icon name="Receipt" className="w-8 h-8 text-primary" />
              <h1 className="text-3xl font-bold text-foreground">Receipt Upload & OCR</h1>
            </div>
            <p className="text-muted-foreground">
              Upload receipt images and extract transaction details using optical character recognition
            </p>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <Icon name="AlertCircle" className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800">Processing Error</p>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
              <button onClick={() => setError(null)} className="text-red-600 hover:text-red-800">
                <Icon name="X" className="w-5 h-5" />
              </button>
            </div>
          )}

          {/* Saved Receipts List */}
          {showReceiptsList && (
            <div className="mb-8 bg-card rounded-xl shadow-elevation-medium p-6 border border-border">
              <h2 className="text-lg font-semibold text-foreground mb-4">Saved Receipts</h2>
              {savedReceipts?.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Icon name="FileText" className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No saved receipts yet</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {savedReceipts?.map(receipt => (
                    <div key={receipt?.id} className="border border-border rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start gap-3 mb-3">
                        <img
                          src={receiptService?.getReceiptImageUrl(receipt?.imagePath)}
                          alt={`Receipt from ${receipt?.merchantName}`}
                          className="w-16 h-16 object-cover rounded border border-border cursor-pointer"
                          onClick={() => handleViewReceipt(receipt)}
                        />
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-foreground truncate">{receipt?.merchantName}</h3>
                          <p className="text-sm text-muted-foreground">
                            {new Date(receipt?.receiptDate)?.toLocaleDateString()}
                          </p>
                          <p className="text-sm font-semibold text-foreground">
                            ${receipt?.totalAmount?.toFixed(2)}
                          </p>
                        </div>
                      </div>
                      {receipt?.transaction && (
                        <div className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                          <Icon name="Link" className="w-3 h-3" />
                          Linked to transaction
                        </div>
                      )}
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewReceipt(receipt)}
                          className="flex-1"
                        >
                          <Icon name="Eye" className="w-3 h-3 mr-1" />
                          View
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteReceipt(receipt?.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Icon name="Trash2" className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column - Upload & Preview */}
            <div className="space-y-6">
              {!imagePreview ? (
                <UploadZone onFileSelect={handleFileSelect} />
              ) : (
                <div className="bg-card rounded-xl shadow-elevation-medium p-6 border border-border">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-foreground">Receipt Preview</h2>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleReset}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <Icon name="X" className="w-4 h-4 mr-2" />
                      Clear
                    </Button>
                  </div>
                  
                  <div className="relative rounded-lg overflow-hidden bg-muted border border-border">
                    <img 
                      src={imagePreview} 
                      alt="Receipt preview" 
                      className="w-full h-auto max-h-[500px] object-contain"
                    />
                  </div>

                  <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Icon name="FileImage" className="w-4 h-4" />
                      <span>{uploadedFile?.name}</span>
                      <span className="ml-auto">{(uploadedFile?.size / 1024)?.toFixed(1)} KB</span>
                    </div>
                  </div>

                  {!isProcessing && !showResults && (
                    <Button
                      onClick={handleProcessReceipt}
                      className="w-full mt-4"
                      size="lg"
                    >
                      <Icon name="Sparkles" className="w-5 h-5 mr-2" />
                      Process Receipt with OCR
                    </Button>
                  )}
                </div>
              )}

              {/* Processing Status */}
              {isProcessing && (
                <ProcessingStatus progress={processingProgress} />
              )}
            </div>

            {/* Right Column - Extracted Data */}
            <div>
              {showResults && extractedData ? (
                <ExtractedDataForm
                  extractedData={extractedData}
                  onSubmit={handleCreateTransaction}
                  onCancel={handleReset}
                />
              ) : (
                <div className="bg-card rounded-xl shadow-elevation-medium p-8 border border-border text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-muted rounded-full mb-4">
                    <Icon name="FileText" className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">No Receipt Processed Yet</h3>
                  <p className="text-sm text-muted-foreground">
                    Upload a receipt image and process it to see extracted transaction details here
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Features Info */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-6 bg-card rounded-lg border border-border">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Icon name="Zap" className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="font-semibold text-foreground">Instant Extraction</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Advanced AI automatically extracts merchant, date, amount, and category from receipt images
              </p>
            </div>

            <div className="p-6 bg-card rounded-lg border border-border">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Icon name="CheckCircle" className="w-5 h-5 text-green-600" />
                </div>
                <h3 className="font-semibold text-foreground">Smart Validation</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Confidence scores for each field help you verify accuracy before creating transactions
              </p>
            </div>

            <div className="p-6 bg-card rounded-lg border border-border">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Icon name="Archive" className="w-5 h-5 text-purple-600" />
                </div>
                <h3 className="font-semibold text-foreground">Receipt Storage</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                All receipts are saved with photos for easy reference and transaction tracking
              </p>
            </div>
          </div>
        </div>
      </main>
      {/* Receipt View Modal */}
      {showReceiptModal && selectedReceipt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowReceiptModal(false)}>
          <div className="bg-card rounded-xl shadow-elevation-high max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e?.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-foreground">Receipt Details</h2>
                <button onClick={() => setShowReceiptModal(false)} className="text-muted-foreground hover:text-foreground">
                  <Icon name="X" className="w-6 h-6" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Receipt Image */}
                <div>
                  <img
                    src={receiptService?.getReceiptImageUrl(selectedReceipt?.imagePath)}
                    alt={`Receipt from ${selectedReceipt?.merchantName}`}
                    className="w-full h-auto rounded-lg border border-border"
                  />
                </div>

                {/* Receipt Details */}
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Merchant</label>
                    <p className="text-lg font-semibold text-foreground">{selectedReceipt?.merchantName}</p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Date</label>
                    <p className="text-foreground">{new Date(selectedReceipt?.receiptDate)?.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Total Amount</label>
                    <p className="text-2xl font-bold text-foreground">${selectedReceipt?.totalAmount?.toFixed(2)}</p>
                  </div>

                  {selectedReceipt?.taxAmount && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Tax Amount</label>
                      <p className="text-foreground">${selectedReceipt?.taxAmount?.toFixed(2)}</p>
                    </div>
                  )}

                  {selectedReceipt?.ocrConfidence && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">OCR Confidence</label>
                      <p className="text-foreground">{Math.round(selectedReceipt?.ocrConfidence * 100)}%</p>
                    </div>
                  )}

                  {selectedReceipt?.transaction && (
                    <div className="p-4 bg-muted/50 rounded-lg">
                      <label className="text-sm font-medium text-muted-foreground mb-2 block">Linked Transaction</label>
                      <p className="text-foreground font-medium">{selectedReceipt?.transaction?.description}</p>
                      <p className="text-sm text-muted-foreground">${Math.abs(selectedReceipt?.transaction?.amount)?.toFixed(2)}</p>
                    </div>
                  )}

                  {selectedReceipt?.notes && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Notes</label>
                      <p className="text-sm text-foreground">{selectedReceipt?.notes}</p>
                    </div>
                  )}

                  <div className="pt-4">
                    <Button
                      onClick={() => setShowReceiptModal(false)}
                      variant="outline"
                      className="w-full"
                    >
                      Close
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReceiptUploadOCRProcessing;